import { useEffect, useRef, useState } from 'react';

/* ===========================================================================
 * HOW THE PALETTE OPENS — the shortcut, and now a button.
 *
 * It opened on Ctrl/⌘+K and nothing else, so it was invisible to anybody who
 * did not already know it was there. The header carries a search trigger now
 * (`HeaderSearch.tsx`), which opens it through `openCommandPalette` — an event
 * rather than shared state, because the two live in different branches of the
 * shell and only the palette needs to know it is open.
 *
 * A palette opened from a button must also hand focus BACK to it when it
 * closes; opened from the keyboard, back to wherever the caret was. Without
 * that, Esc dropped focus on <body> and a keyboard user started over from the
 * top of the page.
 * ======================================================================== */

const OPEN_EVENT = 'command-palette:open';

/** Open the palette from anywhere — the header trigger. */
export function openCommandPalette(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/* THE APP BAR'S FIELD TAKES ⌘K WHEN IT IS ON SCREEN (owner, 2026-10-12: type in
   the field, no popup). It registers a focuser that answers whether it took the
   keystroke; on a phone the field is not drawn, it answers no, and the palette
   opens as before. */
let headerFocus: (() => boolean) | null = null;

/** Register the app bar's search field. Returns the unregister. */
export function registerHeaderSearch(focus: () => boolean): () => void {
  headerFocus = focus;
  return () => {
    if (headerFocus === focus) headerFocus = null;
  };
}

export function useCommandPaletteOpen(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (headerFocus?.()) return;
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    const onOpen = (): void => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      const was = document.activeElement;
      returnTo.current = was instanceof HTMLElement && was !== document.body ? was : null;
    } else if (returnTo.current) {
      // Only if it is still on the page: a pick that navigated may have
      // unmounted it, and the route's own focus handling owns that case.
      if (returnTo.current.isConnected) returnTo.current.focus();
      returnTo.current = null;
    }
  }, [open]);

  return [open, setOpen];
}
